import type { SetupState } from 'api/feedbackInbox.types'
import { useGetRecoveryStatusQuery } from 'api/recoveryAi'
import useCurrentMerchant from 'hooks/useCurrentMerchant'

export interface InboxMountState {
  setupState: SetupState
  collecting: boolean
  // Merchant's written-feedback collection setting (FR-23). Absent means on,
  // matching the column default.
  writtenFeedbackEnabled: boolean
  // First status read in flight (never true while skipped).
  isLoading: boolean
}

// Reads StatusEntity's setup_state/collecting (nexus AI-1404). `collecting`
// falls back to the merchant's own opt-out field because recovery/status
// 404s whenever the recovery-ai flag is off — exactly the merchants most
// likely to have opted out. Missing setup_state, a failed read and a
// skipped one all fail OPEN to pilot, which hides nothing (S5 brief C10).
//   context: `enabled` gates only the request. Both mounts call this hook
//   unconditionally but read it only with the inbox flag on; flag-off is
//   the path production is on, and it must not fire recovery/status.
export function useInboxMountState(enabled = true): InboxMountState {
  const { data: status, isLoading } = useGetRecoveryStatusQuery(undefined, {
    skip: !enabled,
  })
  const merchant = useCurrentMerchant()
  return {
    setupState: status?.setup_state ?? 'pilot',
    collecting:
      status?.collecting ?? merchant?.allow_post_order_feedback_email ?? true,
    writtenFeedbackEnabled: merchant?.allow_written_feedback_collection ?? true,
    isLoading,
  }
}

export interface OrderFeedbackTabInput extends InboxMountState {
  // `access Feedback`, the tab's own gate.
  canAccessFeedback: boolean
  // `access MerchantSettings`, the gate on /recovery_ai (RecoveryAI/index.tsx).
  canAccessMerchantSettings: boolean
}

// Order-feedback Feedback tab (p09-page-shell P3): hidden when written
// feedback collection is off (FR-43), or when RecoveryAI covers every
// location. Held hidden until the first read settles, so the tab never
// appears and then vanishes under a click.
// Exception (AI-1425): at `all` it stays for users who hold `access Feedback`
// but can't open /recovery_ai, since this tab is their only way into the inbox.
export function isOrderFeedbackTabVisible({
  setupState,
  writtenFeedbackEnabled,
  isLoading,
  canAccessFeedback,
  canAccessMerchantSettings,
}: OrderFeedbackTabInput): boolean {
  // Written feedback off hides the tab at every setup_state, the `all`
  // exception included (FR-43): it is the toggle on this page's Settings tab.
  // `collecting` deliberately does NOT gate it (ISS-4285): it reads
  // allow_post_order_feedback_email, one feedback channel rather than the
  // collection switch, so merchants with that email off still receive written
  // feedback, and the Overview's "View all feedback" CTA (which reads only the
  // written toggle) needs this tab to land on.
  if (isLoading || !writtenFeedbackEnabled) return false
  if (setupState !== 'all') return true
  return canAccessFeedback && !canAccessMerchantSettings
}
